'use client';

import { useEffect, useRef, useState } from 'react';
import type { ViewingHint } from '@/lib/ai-search/viewing-hint';
import { ASK_BAR_EVENT, type AskedProduct } from '@/components/products/scrollable-tabs/ask-bar-handoff';

const CARD_SELECTOR = '[data-product-title]';

/** A card holding less of the visible page than this is not "the one on screen". */
export const MIN_VIEW_SHARE = 0.4;

export type CardBox = { title: string; rank?: number; top: number; bottom: number };

/** The card holding the most of the visible span [viewTop, viewBottom], if it holds enough. */
export function dominantCard(cards: readonly CardBox[], viewTop: number, viewBottom: number): CardBox | null {
  const span = viewBottom - viewTop;
  if (span <= 0) return null;
  let best: CardBox | null = null;
  let bestVisible = 0;
  for (const card of cards) {
    const visible = Math.min(card.bottom, viewBottom) - Math.max(card.top, viewTop);
    if (visible > bestVisible) {
      best = card;
      bestVisible = visible;
    }
  }
  return best && bestVisible >= span * MIN_VIEW_SHARE ? best : null;
}

/**
 * The hint after a measure. A press on the chip outranks the screen until the shopper
 * scrolls to another product: `askedOver` is the card that was on screen at the press,
 * which may be a neighbour of the one pressed (its chip peeks out at the bottom).
 */
export function nextViewingHint(
  prev: ViewingHint | null,
  onScreen: Pick<CardBox, 'title' | 'rank'> | null,
  askedOver: string | null,
): ViewingHint | null {
  if (prev?.source === 'ask-button' && onScreen && (onScreen.title === prev.title || onScreen.title === askedOver)) {
    return prev;
  }
  if (!onScreen) return null;
  if (prev?.source === 'viewport' && prev.title === onScreen.title && prev.rank === onScreen.rank) return prev;
  return { title: onScreen.title, ...(onScreen.rank ? { rank: onScreen.rank } : {}), source: 'viewport' };
}

function readCards(doc: Document): CardBox[] {
  return Array.from(doc.querySelectorAll<HTMLElement>(CARD_SELECTOR)).flatMap((el) => {
    const title = el.dataset.productTitle;
    if (!title) return [];
    const { top, bottom } = el.getBoundingClientRect();
    const rank = Number(el.dataset.productRank);
    return [{ title, top, bottom, ...(Number.isInteger(rank) && rank > 0 ? { rank } : {}) }];
  });
}

/**
 * The product the shopper is on, for the search bar to send along with a question:
 * the card filling the screen below the top bar, or the one whose "Ask a question?"
 * chip they pressed. Null on pages without product cards.
 */
export function useViewingProduct(enabled: boolean): ViewingHint | null {
  const [hint, setHint] = useState<ViewingHint | null>(null);
  const onScreenRef = useRef<CardBox | null>(null);
  const askedOverRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const barBottom = document.querySelector('[data-top-bar]')?.getBoundingClientRect().bottom ?? 0;
      const onScreen = dominantCard(readCards(document), Math.max(0, barBottom), window.innerHeight);
      onScreenRef.current = onScreen;
      setHint((prev) => nextViewingHint(prev, onScreen, askedOverRef.current));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const onAsk = (event: Event) => {
      const product = (event as CustomEvent<AskedProduct>).detail;
      if (!product?.title) return;
      askedOverRef.current = onScreenRef.current?.title ?? null;
      setHint({ title: product.title, ...(product.rank ? { rank: product.rank } : {}), source: 'ask-button' });
    };

    // Capture: the cards scroll inside their own containers as well as the page.
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    // Cards that stream in after mount are picked up when the shopper goes to type.
    window.addEventListener('focusin', schedule);
    window.addEventListener(ASK_BAR_EVENT, onAsk);
    schedule();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      window.removeEventListener('focusin', schedule);
      window.removeEventListener(ASK_BAR_EVENT, onAsk);
    };
  }, [enabled]);

  return enabled ? hint : null;
}
